//biome-ignore-all lint/suspicious/noExplicitAny: because
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ActivityLogDto,
  CheckBlockedDomainResponseDto,
  CreatePortalDto,
  LifecycleFilterType,
  PortalDto,
  SetPortalFilteredDomainsDto,
  SetPortalFilteredDomainsResponseDto,
  UpdatePortalDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';
import { getUserDataForResults } from './useFim3Api';

export const usePortalsApi = (httpClient: AxiosInstance) => {
  const baseUrl = `/portals`;
  const queryClient = useQueryClient();
  const getPortalsQueryKey = 'portals';

  const createPortal = async (portal: CreatePortalDto): Promise<PortalDto> => {
    const { data } = await httpClient.post(baseUrl, portal);
    return data;
  };

  const getPortals = async (
    lifecycleFilter?: LifecycleFilterType,
    search?: string,
  ): Promise<PortalDto[]> => {
    const { data } = await httpClient.get(baseUrl, {
      params: { lifecycleFilter, search },
    });

    const userIds = data.map((el: PortalDto) => el.createdBy);

    const result = await getUserDataForResults(
      httpClient,
      userIds,
      data,
      (item: any) => item.createdBy,
    );

    return result as PortalDto[];
  };

  const getPortal = async (id: number): Promise<PortalDto> => {
    const { data } = await httpClient.get(`${baseUrl}/${id}`);
    return data;
  };

  const updatePortal = async (
    updatedPortal: UpdatePortalDto & { id: number },
  ): Promise<PortalDto> => {
    const { data } = await httpClient.put(`${baseUrl}/${updatedPortal.id}`, {
      ...updatedPortal,
      id: undefined,
    });
    return data;
  };

  const removePortal = async (id: number): Promise<PortalDto> => {
    const { data } = await httpClient.delete(`${baseUrl}/${id}`);
    return data;
  };

  const archivePortal = async (id: number): Promise<PortalDto> => {
    const { data } = await httpClient.put(`${baseUrl}/${id}/archive`);
    return data;
  };

  const restorePortal = async (id: number): Promise<PortalDto> => {
    const { data } = await httpClient.put(`${baseUrl}/${id}/restore`);
    return data;
  };

  const useGetAll = (lifecycleFilter?: LifecycleFilterType, search?: string) =>
    useQuery({
      queryKey: [getPortalsQueryKey, 'GetAll', lifecycleFilter, search],
      queryFn: () => getPortals(lifecycleFilter, search),
    });

  const useGetOne = (id: number | undefined) =>
    useQuery({
      queryKey: [getPortalsQueryKey, id],
      queryFn: () => (id ? getPortal(id) : undefined),
      enabled: !!id,
    });

  const useCreate = () =>
    useErrorHandlingMutation({
      mutationFn: createPortal,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [getPortalsQueryKey] });
      },
    });

  const useUpdate = () =>
    useErrorHandlingMutation({
      mutationFn: updatePortal,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [getPortalsQueryKey] });
      },
    });

  const useRemove = () =>
    useErrorHandlingMutation({
      mutationFn: removePortal,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [getPortalsQueryKey] }),
    });

  const useArchive = () =>
    useErrorHandlingMutation({
      mutationFn: archivePortal,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [getPortalsQueryKey] }),
    });

  const useRestore = () =>
    useErrorHandlingMutation({
      mutationFn: restorePortal,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [getPortalsQueryKey] }),
    });

  const getPortalActivityLogs = async (id: number) => {
    const { data } = await httpClient.get(`${baseUrl}/${id}/activity-logs`);

    const userIds = data.map((el: ActivityLogDto) => el.userId);

    const result = await getUserDataForResults(
      httpClient,
      userIds,
      data,
      (item: any) => item.userId,
    );

    return result;
  };

  const useGetActivityLog = (id: number) =>
    useQuery({
      queryKey: [baseUrl, id, 'activity-logs'],
      queryFn: () => getPortalActivityLogs(id),
    });

  const getFilteredDomains = async (
    portalId: number,
  ): Promise<{ domainFilteringEnabled: boolean; domains: string[] }> => {
    const { data } = await httpClient.get(`${baseUrl}/${portalId}/filtered-domains`);
    return data;
  };

  const setFilteredDomains = async (params: {
    portalId: number;
    dto: SetPortalFilteredDomainsDto;
  }): Promise<SetPortalFilteredDomainsResponseDto> => {
    const { data } = await httpClient.put(
      `${baseUrl}/${params.portalId}/filtered-domains`,
      params.dto,
    );
    return data;
  };

  const useGetFilteredDomains = (portalId: number | undefined) =>
    useQuery({
      queryKey: [getPortalsQueryKey, portalId, 'filtered-domains'],
      queryFn: () => (portalId ? getFilteredDomains(portalId) : undefined),
      enabled: !!portalId,
    });

  const useSetFilteredDomains = () =>
    useErrorHandlingMutation({
      mutationFn: setFilteredDomains,
      onSuccess: (_data, variables) =>
        queryClient.invalidateQueries({
          queryKey: [getPortalsQueryKey, variables.portalId, 'filtered-domains'],
        }),
    });

  const checkBlockedDomain = async (domain: string): Promise<CheckBlockedDomainResponseDto> => {
    const { data } = await httpClient.get(`${baseUrl}/check-blocked-domain`, {
      params: { domain },
    });
    return data;
  };

  return {
    useCreate,
    useUpdate,
    useGetOne,
    useGetAll,
    useRemove,
    useArchive,
    useRestore,
    useGetActivityLog,
    useGetFilteredDomains,
    useSetFilteredDomains,
    checkBlockedDomain,
  };
};
